import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, interval } from 'rxjs';
import { Store } from '@ngrx/store';
import { UserService } from '../../core/user/user.service';
import { User } from "../../core/user/user.types";
import { WebSocketEvent } from "../../core/models/api.response.model";
import { insertWebSocketEventData } from "../messaging/store/messaging.actions";
import { AuthService } from "../../core/auth/auth.service";
import * as Sentry from '@sentry/angular-ivy';

const WS_BASE_ENDPOINT: string = 'wss://aeolus.curelinktech.in/v1';
const RECONNECT_INTERVAL: number = 30_000;

export enum WebSocketConnectionStatus {
    connecting = 'connecting',
    open = 'open',
    closed = 'closed'
}

@Injectable({
    providedIn: 'root',
})
export class AeolusService {

    constructor(
        private _store: Store<any>,
        private _userService: UserService,
        private _authService: AuthService
    ) {
        this._userService.user$.subscribe((user: User) => {
            console.log('AeolusService', 'user->', user);
            this.user = user;
            this.setupWSConnection();
            this.startPingTimer();
            this.startVisibilityListener();
        });
        this.socketConnectionStatus = new BehaviorSubject(WebSocketConnectionStatus.connecting);
    }

    private socket$: WebSocketSubject<any>;
    private user: User;
    private lastEventTimestamp: number;

    public socketConnectionStatus: BehaviorSubject<WebSocketConnectionStatus>;


    private setupWSConnection() {
        console.log('setupWSConnection');
        if (this.socket$) {
            try {
                this.socket$.unsubscribe();
            } catch (e) {
                Sentry.captureException(e);
            }
        }
        if (document.hidden) {
            console.log('skipping websocket connection as tab hidden');
            return;
        }
        this.socket$ = webSocket({
            url: `${WS_BASE_ENDPOINT}?user_id=${this.user.id}`,
            openObserver: {
                next: (_) => {
                    this.lastEventTimestamp = Date.now();
                    this.sendPing();
                },
            },
            closeObserver: {
                next: (_) => {
                    this.socketConnectionStatus.next(WebSocketConnectionStatus.closed);
                },
            },
        });
        this.setupWSEventsSubscription();
    }

    startVisibilityListener() {
        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {

                // tab is changed
            } else {
                if (this.socketConnectionStatus.value !== WebSocketConnectionStatus.open) {
                    this.setupWSConnection();
                }
                // tab is active
            }
        });
    }

    startPingTimer() {
        interval(RECONNECT_INTERVAL).subscribe((_) => {
            if (Date.now() - this.lastEventTimestamp > 2 * RECONNECT_INTERVAL) {
                console.log('unsubscribe');
                this.setupWSConnection();
            }
            this.sendPing();
        });
    }

    sendPing() {
        if (!document.hidden) {
            this.socket$.next({ action: 'ping', data: { user_id: this.user.id } });
        } else {
            console.log('skipping ping due as tab is not focused!');
        }
    }

    setupWSEventsSubscription() {
        console.log('setupWSEventsSubscription');
        this.socket$.subscribe({
                next: (msg: WebSocketEvent) => {
                    if (msg.action === 'pong') {
                        this.lastEventTimestamp = Date.now();
                        this._userService.wsConnectionID = msg.connection_id;
                        this.socketConnectionStatus.next(WebSocketConnectionStatus.open);
                        return;
                    } else {
                        if (msg.modified_at && msg.topic_path) {
                            switch (msg.data.event_type) {
                                case 'MessageUpsertV2':
                                case 'ChannelUpsertV2':
                                case 'ChannelUpdateV2':
                                case 'ChannelDeleteV2':
                                    this._store.dispatch(insertWebSocketEventData({
                                        eventData: msg.data,
                                        topic: msg.topic_path,
                                        key: msg.modified_at
                                    }));
                                    break;
                                case 'OpenQueryCountV2':
                                    this._userService.queryCount = msg.data.open_query_count;
                                    break;
                                case 'IncomingCallV2':
                                    break;
                            }
                        }
                    }
                },
                error:
                    (err) => {
                        console.log(err);
                        // this.setupWSConnection();
                    },
                complete:
                    () => {
                        console.log('Websocket$ Complete');
                        this.setupWSConnection();
                    }
            }
        );
    }
}
